import React, { useState, useEffect } from 'react';
import { Eye, Download, CreditCard, Search, Filter, Calendar, AlertCircle, Printer, Send } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate, downloadPDF, sendEmail } from '../../lib/utils';
import { invoiceService } from '../../lib/database/invoices';
import { useAuth } from '../../hooks/useAuth';
import { ViewInvoiceModal } from './ViewInvoiceModal';
import { PrintableInvoice } from '../print/PrintableInvoice';
import { StripePaymentModal } from '../payment/StripePaymentModal';

export const ClientInvoices: React.FC = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [invoiceToPrint, setInvoiceToPrint] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState<{[key: string]: boolean}>({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [invoiceToPayId, setInvoiceToPayId] = useState<string | null>(null);
  const [invoiceToPayNumber, setInvoiceToPayNumber] = useState<string>('');
  const [invoiceToPayAmount, setInvoiceToPayAmount] = useState<number>(0);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const { data } = await invoiceService.getInvoices();
      if (data) {
        // Filter invoices for current client
        const clientInvoices = data.filter(invoice => 
          invoice.client?.email === user?.email
        );
        setInvoices(clientInvoices);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchInvoices();
    }
  }, [user?.email]);

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handlePrintInvoice = (invoice: any) => {
    setInvoiceToPrint(invoice);
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadPDF = async (invoice: any) => {
    setActionLoading({...actionLoading, [`download_${invoice.id}`]: true});
    try {
      // Generate and download PDF
      const filename = `Invoice_${invoice.invoice_number}`;
      const content = JSON.stringify({
        invoiceNumber: invoice.invoice_number,
        client: invoice.client?.name,
        amount: invoice.total,
        date: invoice.created_at,
        dueDate: invoice.due_date,
        status: invoice.status
      });
      
      await downloadPDF(filename, content);
      
      // Show success message
      alert("Invoice PDF downloaded successfully");
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setActionLoading({...actionLoading, [`download_${invoice.id}`]: false});
    }
  };

  const handleRequestInvoiceCopy = async (invoice: any) => {
    setActionLoading({...actionLoading, [`email_${invoice.id}`]: true});
    try {
      // Send email with invoice copy
      const userEmail = user?.email || invoice.client?.email || '';
      const subject = `Copy of Invoice ${invoice.invoice_number}`;
      const body = `As requested, please find attached a copy of your invoice ${invoice.invoice_number} for ${formatCurrency(Number(invoice.total))}.`;
      
      await sendEmail(userEmail, subject, body);
      
      // Show success message
      alert(`Invoice copy has been sent to ${userEmail}`);
    } catch (error) {
      console.error('Error sending invoice copy:', error);
      alert('Failed to send invoice copy. Please try again.');
    } finally {
      setActionLoading({...actionLoading, [`email_${invoice.id}`]: false});
    }
  };

  const handlePayInvoice = async (invoice: any) => {
    setActionLoading({...actionLoading, [`pay_${invoice.id}`]: true});
    try {
      // Set up payment modal
      setInvoiceToPayId(invoice.id);
      setInvoiceToPayNumber(invoice.invoice_number);
      setInvoiceToPayAmount(Number(invoice.total));
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment. Please try again.');
    } finally {
      setActionLoading({...actionLoading, [`pay_${invoice.id}`]: false});
    }
  };

  const handlePaymentSuccess = async () => {
    try {
      if (invoiceToPayId) {
        // Mark invoice as paid
        await invoiceService.markAsPaid(invoiceToPayId);
        alert('Payment successful! Invoice has been marked as paid.');
        fetchInvoices(); // Refresh the list
      }
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      alert('Payment was successful, but failed to update invoice status. Please contact support.');
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || invoice.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  const totalOwed = invoices
    .filter(inv => inv.status !== 'paid')
    .reduce((sum, inv) => sum + Number(inv.total), 0);

  const overdueInvoices = invoices.filter(inv => 
    inv.status === 'overdue' || 
    (inv.status === 'sent' && new Date(inv.due_date) < new Date())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Invoices</h1>
            <p className="text-gray-600 mt-2">View and manage your invoices</p>
          </div>
          {totalOwed > 0 && (
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Outstanding</p>
              <p className="text-2xl font-bold text-error-600">{formatCurrency(totalOwed)}</p>
            </div>
          )}
        </div>

        {/* Overdue Alert */}
        {overdueInvoices.length > 0 && (
          <Card className="bg-error-50 border-error-200">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-error-600" />
              <div>
                <h3 className="font-semibold text-error-800">Overdue Invoices</h3>
                <p className="text-error-700">
                  You have {overdueInvoices.length} overdue invoice{overdueInvoices.length !== 1 ? 's' : ''} 
                  totaling {formatCurrency(overdueInvoices.reduce((sum, inv) => sum + Number(inv.total), 0))}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Search and Filters */}
        <Card padding="sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter:</span>
              </div>
              <div className="flex space-x-2">
                {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      filter === status
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Invoices List */}
        <div className="space-y-4">
          {filteredInvoices.length === 0 ? (
            <Card className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filter !== 'all' ? 'No invoices found' : 'No invoices yet'}
              </h3>
              <p className="text-gray-600">
                {searchTerm || filter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Your invoices will appear here when they are created'
                }
              </p>
            </Card>
          ) : (
            filteredInvoices.map((invoice) => (
              <Card key={invoice.id} hover className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{invoice.invoice_number}</h3>
                      <p className="text-gray-600">Due {formatDate(invoice.due_date)}</p>
                      <p className="text-sm text-gray-500">Created {formatDate(invoice.created_at)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(Number(invoice.total))}</p>
                      <StatusBadge status={invoice.status} />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Eye}
                        onClick={() => handleViewInvoice(invoice)}
                      >
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Printer}
                        onClick={() => handlePrintInvoice(invoice)}
                      >
                        Print
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Download}
                        onClick={() => handleDownloadPDF(invoice)}
                        loading={actionLoading[`download_${invoice.id}`]}
                        disabled={actionLoading[`download_${invoice.id}`]}
                      >
                        Download PDF
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Send}
                        onClick={() => handleRequestInvoiceCopy(invoice)}
                        loading={actionLoading[`email_${invoice.id}`]}
                        disabled={actionLoading[`email_${invoice.id}`]}
                      >
                        Email Invoice
                      </Button>
                      {invoice.status !== 'paid' && (
                        <Button 
                          variant="primary" 
                          size="sm" 
                          icon={CreditCard}
                          onClick={() => handlePayInvoice(invoice)}
                          loading={actionLoading[`pay_${invoice.id}`]}
                          disabled={actionLoading[`pay_${invoice.id}`]}
                          className="bg-success-600 hover:bg-success-700"
                        >
                          Pay Now
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                {invoice.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600">{invoice.notes}</p>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card padding="sm" className="text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <p className="text-2xl font-bold text-blue-700">{invoices.length}</p>
            <p className="text-sm text-blue-600">Total Invoices</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-success-50 to-success-100 border-success-200">
            <p className="text-2xl font-bold text-success-700">
              {invoices.filter(i => i.status === 'paid').length}
            </p>
            <p className="text-sm text-success-600">Paid</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-warning-50 to-warning-100 border-warning-200">
            <p className="text-2xl font-bold text-warning-700">
              {invoices.filter(i => i.status === 'sent').length}
            </p>
            <p className="text-sm text-warning-600">Pending</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-error-50 to-error-100 border-error-200">
            <p className="text-2xl font-bold text-error-700">
              {overdueInvoices.length}
            </p>
            <p className="text-sm text-error-600">Overdue</p>
          </Card>
        </div>

        {/* View Invoice Modal */}
        <ViewInvoiceModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedInvoice(null);
            fetchInvoices(); // Refresh after closing modal in case status changed
          }}
          invoice={selectedInvoice}
        />

        {/* Payment Modal */}
        <StripePaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          invoiceId={invoiceToPayId || ''}
          invoiceNumber={invoiceToPayNumber}
          amount={invoiceToPayAmount}
        />
      </div>

      {/* Printable Version */}
      <PrintableInvoice invoice={invoiceToPrint} isVisible={showPrintView} />
    </>
  );
};